import { InteractionRequiredAuthError } from "@azure/msal-browser";

import { apiRequest, azureAdBackendClientId } from "../config/authConfig";
import { ensureMsalInitialized, msalInstance } from "../config/msalInstance";

/**
 * Silent access-token acquisition for the API client.
 *
 * Aligned with the E2E reference (e2e-arch-mapping-frontend/src/auth/apiClient.ts):
 * - Caches token in memory; concurrent requests share one acquireTokenSilent call.
 * - No permanent failure flag — resets on each cycle so a re-login is always retried.
 * - On InteractionRequiredAuthError (stale session, new scope, MFA) triggers a
 *   loginRedirect so the user gets a fresh authorization_code exchange and MSAL
 *   caches the backend access token before any API requests fire again.
 *
 * Returns null when no backend client ID is configured (pass-through dev auth).
 */

let cachedToken: string | null = null;
let tokenExpiresAt = 0;
let tokenPromise: Promise<string | null> | null = null;
const REFRESH_BUFFER_MS = 5 * 60 * 1000;
const TOKEN_REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const IS_DEV = import.meta.env.DEV;
let refreshIntervalId: ReturnType<typeof setInterval> | null = null;
let visibilityListenerRegistered = false;

type AcquireTokenOptions = {
  allowInteraction: boolean;
  forceRefresh?: boolean;
};

function redirectToLogin(): void {
  void msalInstance.loginRedirect({ scopes: apiRequest.scopes }).catch((err) => {
    if (IS_DEV) {
      console.error("[accessToken] loginRedirect failed:", err);
    }
  });
}

function handleVisibilityChange(): void {
  if (document.visibilityState !== "visible" || !azureAdBackendClientId) return;
  void refreshAccessToken().catch((err) => {
    if (IS_DEV) {
      console.error("[accessToken] visibility refresh failed:", err);
    }
  });
}

function registerVisibilityListener(): void {
  if (visibilityListenerRegistered || typeof document === "undefined") return;
  document.addEventListener("visibilitychange", handleVisibilityChange);
  visibilityListenerRegistered = true;
}

function unregisterVisibilityListener(): void {
  if (!visibilityListenerRegistered || typeof document === "undefined") return;
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  visibilityListenerRegistered = false;
}

/** Acquire a backend-audienced access token for the signed-in account, or null. */
async function acquireAccessToken({
  allowInteraction,
  forceRefresh = false,
}: AcquireTokenOptions): Promise<string | null> {
  // No backend client ID = dev pass-through auth; skip MSAL entirely.
  if (!azureAdBackendClientId) return null;

  if (!forceRefresh && cachedToken && Date.now() < tokenExpiresAt - REFRESH_BUFFER_MS) {
    return cachedToken;
  }
  if (tokenPromise) {
    return tokenPromise;
  }

  tokenPromise = (async () => {
    try {
      if (IS_DEV) {
        console.log("[accessToken] acquiring token for scopes:", apiRequest.scopes);
      }
      const msalAcquire = (async () => {
        await ensureMsalInitialized();
        const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
        if (IS_DEV) {
          console.log("[accessToken] active account present:", account != null);
        }
        if (!account) return null;
        return msalInstance.acquireTokenSilent({ ...apiRequest, account });
      })();
      const result = await msalAcquire;

      if (!result) {
        if (IS_DEV) {
          console.warn("[accessToken] result is null — no account or token unavailable");
        }
        return null;
      }

      if (IS_DEV) {
        console.log("[accessToken] token acquired, expires:", result.expiresOn);
      }
      cachedToken = result.accessToken;
      tokenExpiresAt = result.expiresOn
        ? result.expiresOn.getTime()
        : Date.now() + 60 * 60 * 1000;
      return cachedToken;
    } catch (err) {
      if (IS_DEV) {
        console.error("[accessToken] acquireTokenSilent failed:", err);
      }
      cachedToken = null;
      tokenExpiresAt = 0;

      if (err instanceof InteractionRequiredAuthError) {
        if (IS_DEV) {
          console.warn(
            allowInteraction
              ? "[accessToken] interaction required — redirecting to login with API scope"
              : "[accessToken] interaction required during background refresh"
          );
        }
        if (allowInteraction) {
          redirectToLogin();
        }
      }
      // Return null; the request proceeds without a token and the backend
      // returns 401, which the response interceptor handles.
      return null;
    } finally {
      tokenPromise = null;
    }
  })();

  return tokenPromise;
}

/** Acquire a token for a foreground API request. */
export async function getAccessToken(): Promise<string | null> {
  return acquireAccessToken({ allowInteraction: true });
}

/** Refresh the token without redirecting if silent acquisition needs interaction. */
export async function refreshAccessToken(): Promise<string | null> {
  clearCachedToken();
  return acquireAccessToken({ allowInteraction: false, forceRefresh: true });
}

/**
 * Keep the MSAL/API token warm while the authenticated app is open. Browser
 * timer throttling is handled by refreshing again when the page becomes visible.
 */
export function startTokenRefresh(): void {
  if (!azureAdBackendClientId || refreshIntervalId !== null) return;
  registerVisibilityListener();
  refreshIntervalId = setInterval(() => {
    void refreshAccessToken().catch((err) => {
      if (IS_DEV) {
        console.error("[accessToken] background refresh failed:", err);
      }
    });
  }, TOKEN_REFRESH_INTERVAL_MS);
}

/** Stop proactive refresh without affecting the current cached token. */
export function stopTokenRefresh(): void {
  if (refreshIntervalId !== null) {
    clearInterval(refreshIntervalId);
    refreshIntervalId = null;
  }
  unregisterVisibilityListener();
}

/** Invalidate the cached token (e.g. on 401 or logout). */
export function clearCachedToken(): void {
  cachedToken = null;
  tokenExpiresAt = 0;
}
